<template>
  <v-row class="mt-1">
    <v-col>
      <ValidationObserver ref="lotForm" v-slot="{ invalid, validated }">
        <v-expansion-panels multiple>

          <v-expansion-panel v-test="{ panel: '' }">
            <ValidationObserver ref="info" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="info">
                  <v-icon>
                    info
                  </v-icon>
                  {{ $t('Общая информация') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2 pt-0">
                    <validatable-text-input
                        v-model="lot.lotDescription"
                        :readonly="readonly"
                        rules="required"
                        :label="$t('Предмет договора')" />
                  </v-col>
                  <v-col
                      cols="6"
                      md="6"
                      class="pl-2">
                    <validatable-date-picker
                        v-model="lot.publicationDate"
                        v-test="{ date: 'publicationDate' }"
                        rules="required"
                        dense
                        :label="$t('Дата публикации')" />
                  </v-col>
                  <v-col
                      cols="12"
                      class="pl-2">
                    <validatable-enum-combobox
                        v-model="lot.premiumType"
                        :values-enum="PREMIUM_TYPE_ENUM"
                        rules="required"
                        dense
                        :readonly="readonly"
                        :clearable="!readonly"
                        :label="$t('Способ расчета цены')" />
                  </v-col>
                  <v-col
                      cols="12"
                      class="pl-2">
                    <validatable-text-input
                        v-if="isFixPremium"
                        v-model="lot.currency"
                        rules="required"
                        :readonly="readonly"
                        :clearable="!readonly"
                        :label="$t('Валюта контракта')" />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>

          <v-expansion-panel v-test="{ panel: '' }">
            <ValidationObserver ref="incoterms" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="incoterms">
                  <v-icon>
                    mdi-routes
                  </v-icon>
                  {{ $t('Условия и базис поставки Incoterms') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row>
                  <v-col
                      cols="12"
                      md="12"
                      class="pl-2">
                    <validatable-incoterm-selection
                        v-model="lot.incotermId"
                        :label="$t('Условия поставки')"
                        rules="required"
                        :readonly="readonly"
                        @change="changeIncoterm" />
                  </v-col>
                  <v-col cols="12" md="12">
                    <div v-if="hasBasises">
                      <general-error v-if="!!generalError" />
                      <validatable-delivery-basises
                          v-if="hasBasises"
                          v-model="lot.lotDeliveryBasises"
                          :incoterm-id="lot.incotermId" />
                    </div>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>

          <v-expansion-panel v-test="{ panel: '' }">
            <ValidationObserver ref="contacts" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="contact_mail">
                  <v-icon>
                    contact_mail
                  </v-icon>
                  {{ $t('Контактная информация') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2">
                    <validatable-text-input
                        v-model="lot.contactPerson"
                        v-test="{ input: 'contactPerson' }"
                        prepend-icon="person"
                        rules="required"
                        dense
                        :label="$t('Контактное лицо')" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2">
                    <validatable-text-input
                        v-model="lot.contactPhone"
                        v-test="{ input: 'contactPhone' }"
                        dense
                        :rules="{ required: true,
                                  regex: /^[+]?[\d\s(\-);,]+$/ }"
                        :label="$t('Контактный телефон')"
                        prepend-icon="settings_phone"
                        :type="'tel'" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2">
                    <validatable-text-input
                        v-model="lot.contactEmail"
                        v-test="{ input: 'contactEmail' }"
                        dense
                        rules="required|email"
                        :label="$t('Электронный адрес')"
                        prepend-icon="alternate_email" />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>

        </v-expansion-panels>
        <v-alert
            class="text-center"
            :value="invalid && validated"
            type="error">
          {{ $t('Не все поля заполнены.') }}
        </v-alert>
      </ValidationObserver>
    </v-col>
  </v-row>
</template>

<script>
  import Vue from 'vue'
  import lodash from 'lodash'
  import ValidatableDatePicker from '@/components/reused/ui/ValidatableDatePicker.vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import ValidatableEnumCombobox from '@/components/reused/ui/ValidatableEnumCombobox.vue'
  import ValidatableIncotermSelection from '@/components/reused/ui/ValidatableIncotermSelection.vue'
  import ValidatableDeliveryBasises from '@/components/reused/ui/ValidatableDeliveryBasises.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import { DICTIONARY_TYPES, VAT_TEXT, PREMIUM_TYPES } from '@/constants/business'
  import { PREMIUM_TYPE_ENUM } from '@/constants/enums'
  import { DICTIONARIES } from '@/constants/store.js'
  import { mapState, mapActions } from 'vuex'

  export default {
    components: {
      ValidatableDatePicker,
      ValidatableTextInput,
      ValidatableEnumCombobox,
      ValidatableIncotermSelection,
      ValidatableDeliveryBasises,
      GeneralError
    },
    props: {
      readonly: {
        type: Boolean,
        default: false
      },
      saveMethod: {
        type: Function
      },
      lotInfo: {
        type: Object,
        required: true
      }
    },
    data () {
      return {
        DICTIONARY_TYPES,
        PREMIUM_TYPE_ENUM,
        VAT_TEXT,
        lot: {
          creatorId: null,
          lotDescription: null,
          publicationDate: null,
          premiumType: null,
          currency: null,
          contactPerson: null,
          contactPhone: null,
          contactEmail: null,
          incotermId: null,
          lotDeliveryBasises: []
        }
      }
    },
    computed: {
      ...mapState(DICTIONARIES.NAMESPACE, {
        incoterms: state => state[DICTIONARY_TYPES.INCOTERMS] || [],
        deliveryBasises: state => {
          return state[DICTIONARY_TYPES.DELIVERY_BASISES] || []
        }
      }),
      hasChanges () {
        return Object.keys(this.lot).some(key => {
          if (key === 'lotDeliveryBasises') {
            return !lodash.isEqual(this.lot.lotDeliveryBasises, this.lotInfo.lotDeliveryBasises)
          }
          return this.lot[key] !== this.lotInfo[key]
        })
      },
      isFixPremium () {
        return this.lot.premiumType === PREMIUM_TYPES.FIX
      },
      hasBasises () {
        if (this.lot.incotermId) {
          return this.incoterms.some(i => i.incotermId === this.lot.incotermId && i.hasBasises)
        }
        return false
      }
    },
    watch: {
      lotInfo: {
        deep: true,
        immediate: true,
        handler: function (newVal) {
          this.mapFields(newVal)
        }
      }
    },
    beforeDestroy () {

    },
    created () {
    },
    methods: {
      ...mapActions(DICTIONARIES.NAMESPACE, {
        getBusinessDictionaryItemsAction: DICTIONARIES.ACTIONS.GET_BUSINESS_DICTIONARY_ITEMS,
        clearDictionaryItemsAction: DICTIONARIES.ACTIONS.CLEAR_DICTIONARY_ITEMS
      }),
      validateAndSave: async function () {
        const result = await this.$refs.lotForm.validate()
        if (!result) {
          throw new Vue.errorsTypes.ValidationError()
        }
        // если изменений нет, то сохранение не нужно
        if (this.readonly || !this.hasChanges) {
          return
        }
        if (this.saveMethod) {
          await this.saveMethod(this.lot)
        }
      },
      mapFields: async function (lot) {
        if (!lot.lotId) {
          return
        }

        if (this.lot.incotermId !== lot.incotermId) {
          await this.getBusinessDictionaryItemsAction({ dictionaryType: DICTIONARY_TYPES.DELIVERY_BASISES, incotermId: lot.incotermId })
        }

        for (const key in this.lot) {
          this.lot[key] = lodash.cloneDeep(lot[key])
        }

        if (lot.premiumType === PREMIUM_TYPES.DIFFERENTIAL) {
          this.lot.currency = null
        }
      },
      changeIncoterm: async function (item) {
        await this.getBusinessDictionaryItemsAction({ dictionaryType: DICTIONARY_TYPES.DELIVERY_BASISES, incotermId: item.incotermId })
        this.lot.lotDeliveryBasises = lodash.cloneDeep(this.deliveryBasises)
      }
    }
  }
</script>
